/**
 * Schematyczne mockupy UI Safari (iOS) i Chrome (Android) z podświetlonym
 * przyciskiem, który user ma tapnąć. Inline SVG zamiast PNG — skalują się
 * idealnie i nie zwiększają liczby plików w `/public`.
 *
 * Kolory przez CSS vars (`--kd-card`, `--kd-line`, `--kd-ink`, `--kd-muted`,
 * `--kd-accent`) → ilustracja respektuje wybrany przez usera accent.
 *
 * Każdy komponent ma `viewBox` i `width=100%` — wpisuje się w kontener
 * o dowolnej szerokości. Wysokość proporcjonalna.
 */

const FRAME_RADIUS = 14;

function Arrow({ x, y }: { x: number; y: number }) {
  // Pulsująca strzałka wskazująca highlight. Drobny `animate` pokazuje że
  // to akcja do wykonania, ale nie odciąga uwagi od ilustracji.
  return (
    <g transform={`translate(${x} ${y})`}>
      <path
        d="M0 -10 L0 4 M-4 0 L0 4 L4 0"
        stroke="var(--kd-accent)"
        strokeWidth="2"
        fill="none"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <animate
          attributeName="opacity"
          values="0.4;1;0.4"
          dur="1.6s"
          repeatCount="indefinite"
        />
      </path>
    </g>
  );
}

/** iOS Safari toolbar z podświetloną ikoną Udostępnij. */
export function IosShareIllustration() {
  return (
    <svg
      viewBox="0 0 240 130"
      className="w-full h-auto"
      role="img"
      aria-label="Pasek dolny Safari z przyciskiem Udostępnij"
    >
      {/* Ramka */}
      <rect
        x="2"
        y="2"
        width="236"
        height="126"
        rx={FRAME_RADIUS}
        fill="var(--kd-card)"
        stroke="var(--kd-line)"
        strokeWidth="1.5"
      />
      {/* Wirtualny "ekran" treści */}
      <rect
        x="14"
        y="14"
        width="212"
        height="64"
        rx="8"
        fill="var(--kd-soft, #F4F1EA)"
      />
      <circle cx="120" cy="42" r="10" fill="var(--kd-accent)" opacity="0.25" />
      <text
        x="120"
        y="60"
        textAnchor="middle"
        fontSize="8"
        fill="var(--kd-muted)"
      >
        sianko.app
      </text>

      {/* Separator toolbara */}
      <line
        x1="14"
        y1="92"
        x2="226"
        y2="92"
        stroke="var(--kd-line)"
        strokeWidth="1"
      />

      {/* Ikony toolbara: ◁ ▷ [share] 📖 📑 — równo rozmieszczone */}
      {[
        { x: 36, label: "‹", muted: true },
        { x: 72, label: "›", muted: true },
        { x: 120, label: "share" }, // highlighted
        { x: 168, label: "▢", muted: true },
        { x: 204, label: "⊞", muted: true },
      ].map((icon, i) => {
        if (icon.label === "share") {
          return (
            <g key={i}>
              {/* Highlight pierścień */}
              <circle
                cx={icon.x}
                cy="108"
                r="13"
                fill="var(--kd-accent)"
                opacity="0.18"
              />
              <circle
                cx={icon.x}
                cy="108"
                r="13"
                fill="none"
                stroke="var(--kd-accent)"
                strokeWidth="1.5"
              />
              {/* Ikona share: kwadracik + strzałka w górę */}
              <g
                stroke="var(--kd-accent)"
                strokeWidth="1.6"
                fill="none"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d={`M${icon.x} 100 L${icon.x} 112`} />
                <path
                  d={`M${icon.x - 3} 103 L${icon.x} 100 L${icon.x + 3} 103`}
                />
                <path
                  d={`M${icon.x - 5} 108 L${icon.x - 5} 116 L${icon.x + 5} 116 L${icon.x + 5} 108`}
                />
              </g>
            </g>
          );
        }
        return (
          <text
            key={i}
            x={icon.x}
            y="113"
            textAnchor="middle"
            fontSize="13"
            fill="var(--kd-muted)"
          >
            {icon.label}
          </text>
        );
      })}

      <Arrow x={120} y={86} />
    </svg>
  );
}

/** iOS Share sheet — lista akcji z podświetlonym "Dodaj do ekranu początkowego". */
export function IosAddHomeIllustration() {
  const items = [
    { label: "Kopiuj", icon: "⎘" },
    { label: "Dodaj do listy", icon: "+" },
    { label: "Dodaj do ekranu początkowego", icon: "+", highlight: true },
    { label: "Znajdź na stronie", icon: "⌕" },
  ];
  return (
    <svg
      viewBox="0 0 240 160"
      className="w-full h-auto"
      role="img"
      aria-label="Menu Udostępniania z opcją Dodaj do ekranu początkowego"
    >
      <rect
        x="2"
        y="2"
        width="236"
        height="156"
        rx={FRAME_RADIUS}
        fill="var(--kd-card)"
        stroke="var(--kd-line)"
        strokeWidth="1.5"
      />
      {/* Drag handle */}
      <rect x="110" y="10" width="20" height="3" rx="1.5" fill="var(--kd-line)" />

      {items.map((it, i) => {
        const y = 30 + i * 30;
        return (
          <g key={i}>
            {it.highlight && (
              <rect
                x="10"
                y={y - 12}
                width="220"
                height="26"
                rx="8"
                fill="var(--kd-accent)"
                opacity="0.15"
              />
            )}
            {/* Ikona kółko */}
            <circle
              cx="28"
              cy={y}
              r="9"
              fill="none"
              stroke={
                it.highlight ? "var(--kd-accent)" : "var(--kd-line)"
              }
              strokeWidth="1.4"
            />
            <text
              x="28"
              y={y + 4}
              textAnchor="middle"
              fontSize="11"
              fill={it.highlight ? "var(--kd-accent)" : "var(--kd-muted)"}
              fontWeight={it.highlight ? "600" : "400"}
            >
              {it.icon}
            </text>
            <text
              x="46"
              y={y + 4}
              fontSize="10"
              fill={it.highlight ? "var(--kd-ink)" : "var(--kd-muted)"}
              fontWeight={it.highlight ? "700" : "400"}
            >
              {it.label}
            </text>
            {/* Separator pomiędzy itemami (ostatni bez) */}
            {i < items.length - 1 && (
              <line
                x1="46"
                y1={y + 14}
                x2="226"
                y2={y + 14}
                stroke="var(--kd-line)"
                strokeWidth="0.8"
              />
            )}
          </g>
        );
      })}

      <Arrow x={20} y={84} />
    </svg>
  );
}

/** Górny pasek Chrome (Android) z podświetlonym menu ⋮. */
export function AndroidMenuIllustration() {
  return (
    <svg
      viewBox="0 0 240 130"
      className="w-full h-auto"
      role="img"
      aria-label="Pasek górny Chrome z przyciskiem menu"
    >
      <rect
        x="2"
        y="2"
        width="236"
        height="126"
        rx={FRAME_RADIUS}
        fill="var(--kd-card)"
        stroke="var(--kd-line)"
        strokeWidth="1.5"
      />
      {/* Pasek toolbara Chrome u góry */}
      <rect
        x="14"
        y="14"
        width="212"
        height="28"
        rx="14"
        fill="var(--kd-soft, #F4F1EA)"
      />
      {/* Tab counter */}
      <rect
        x="22"
        y="22"
        width="14"
        height="12"
        rx="2"
        fill="none"
        stroke="var(--kd-muted)"
        strokeWidth="1.2"
      />
      <text x="29" y="31" textAnchor="middle" fontSize="7" fill="var(--kd-muted)">
        2
      </text>
      {/* URL bar */}
      <text x="50" y="32" fontSize="9" fill="var(--kd-muted)">
        sianko.app
      </text>
      {/* Menu ⋮ — highlighted */}
      <circle cx="208" cy="28" r="13" fill="var(--kd-accent)" opacity="0.18" />
      <circle
        cx="208"
        cy="28"
        r="13"
        fill="none"
        stroke="var(--kd-accent)"
        strokeWidth="1.5"
      />
      <g fill="var(--kd-accent)">
        <circle cx="208" cy="22" r="1.6" />
        <circle cx="208" cy="28" r="1.6" />
        <circle cx="208" cy="34" r="1.6" />
      </g>

      {/* Treść strony — placeholder */}
      <rect
        x="14"
        y="54"
        width="212"
        height="60"
        rx="8"
        fill="var(--kd-soft, #F4F1EA)"
      />
      <circle cx="120" cy="80" r="10" fill="var(--kd-accent)" opacity="0.25" />

      <Arrow x={208} y={56} />
    </svg>
  );
}

/** Otwarte menu Chrome z podświetlonym "Zainstaluj aplikację". */
export function AndroidInstallIllustration() {
  const items = [
    { label: "Nowa karta", icon: "+" },
    { label: "Historia", icon: "↺" },
    { label: "Pobrane", icon: "↓" },
    { label: "Zainstaluj aplikację", icon: "⊕", highlight: true },
    { label: "Ustawienia", icon: "⚙" },
  ];
  return (
    <svg
      viewBox="0 0 240 180"
      className="w-full h-auto"
      role="img"
      aria-label="Menu Chrome z opcją Zainstaluj aplikację"
    >
      <rect
        x="2"
        y="2"
        width="236"
        height="176"
        rx={FRAME_RADIUS}
        fill="var(--kd-card)"
        stroke="var(--kd-line)"
        strokeWidth="1.5"
      />
      {/* Mini wskazówka skąd menu wychodzi — kropki w prawym górnym */}
      <g fill="var(--kd-muted)">
        <circle cx="220" cy="14" r="1.4" />
        <circle cx="220" cy="20" r="1.4" />
        <circle cx="220" cy="26" r="1.4" />
      </g>

      {/* Lista pozycji menu */}
      {items.map((it, i) => {
        const y = 46 + i * 24;
        return (
          <g key={i}>
            {it.highlight && (
              <rect
                x="14"
                y={y - 11}
                width="212"
                height="22"
                rx="6"
                fill="var(--kd-accent)"
                opacity="0.15"
              />
            )}
            <text
              x="30"
              y={y + 4}
              textAnchor="middle"
              fontSize="13"
              fill={it.highlight ? "var(--kd-accent)" : "var(--kd-muted)"}
              fontWeight={it.highlight ? "700" : "400"}
            >
              {it.icon}
            </text>
            <text
              x="48"
              y={y + 4}
              fontSize="11"
              fill={it.highlight ? "var(--kd-ink)" : "var(--kd-muted)"}
              fontWeight={it.highlight ? "700" : "400"}
            >
              {it.label}
            </text>
          </g>
        );
      })}

      <Arrow x={24} y={108} />
    </svg>
  );
}
